import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Program {
  id: string
  name: string
  program_type: string
  description: string
  provider: string
  start_date: string
  end_date: string
  max_participants: number
  current_participants: number
  points_value: number
  status: string
}

interface Challenge {
  id: string
  name: string
  challenge_type: string
  goal: number
  unit: string
  start_date: string
  end_date: string
  participants_count: number
  prize: string
  status: string
}

export default function WellnessPage() {
  const [programs, setPrograms] = useState<Program[]>([])
  const [challenges, setChallenges] = useState<Challenge[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [progRes, chalRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/wellness/programs'),
      fetch(API_BASE + '/wellness/challenges'),
      fetch(API_BASE + '/wellness/statistics')
    ])
    if (progRes.ok) setPrograms((await progRes.json()).programs || [])
    if (chalRes.ok) setChallenges((await chalRes.json()).challenges || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'completed': case 'achieved': return 'bg-green-100 text-green-800'
      case 'upcoming': return 'bg-blue-100 text-blue-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Wellness Programs</h1>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Join Program</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Programs</div>
              <div className="text-2xl font-bold">{statistics.active_programs}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Challenges</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.active_challenges}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Participants</div>
              <div className="text-2xl font-bold text-green-600">{statistics.total_participants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Participation Rate</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.participation_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Wellness Score</div>
              <div className="text-2xl font-bold">{statistics.avg_wellness_score}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Wellness Programs</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {programs.slice(0, 12).map(prog => (
                  <tr key={prog.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{prog.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{prog.program_type.replace(/_/g, ' ')} - {prog.provider}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{prog.start_date}</div>
                      <div className="text-xs text-gray-500">to {prog.end_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{prog.current_participants}/{prog.max_participants}</div>
                      <div className="text-xs text-green-600">{prog.points_value} points</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(prog.status)}`}>{prog.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Challenges</h3>
            <div className="space-y-3">
              {challenges.filter(c => c.status === 'active').slice(0, 10).map(chal => (
                <div key={chal.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{chal.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(chal.status)}`}>{chal.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{chal.challenge_type} - {chal.goal.toLocaleString()} {chal.unit}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{chal.participants_count} participants</span>
                    <span className="text-xs text-green-600">{chal.prize}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
